import { z } from 'zod';

import { HTTPError, type MaybeHTTPError } from './HTTPError';

/**
 * Sanitize an error message for a user.
 * This method should be used to render an error.
 */
export function sanitizeError(error: MaybeHTTPError, defaultMessage = 'Unexpected error'): string {
    if (error instanceof HTTPError && error.expose) {
        return error.message;
    }

    return defaultMessage;
}

/**
 * Schema to extract HTTPError from tRPC error data.
 */
const HTTPErrorDataSchema = z.object({
    httpError: z.object({
        message: z.string(),
        statusCode: z.number(),
        expose: z.boolean().optional(),
    }),
});

/**
 * Try to extract an HTTPError from the error data, if it exists.
 * This is used to extract HTTPError that have been serialized through tRPC.
 */
function getHTTPErrorFromData(error: MaybeHTTPError): HTTPError | null {
    if (error instanceof Error && 'data' in error) {
        const parseResult = HTTPErrorDataSchema.safeParse(error.data);
        if (parseResult.success) {
            const { message, statusCode, expose = false } = parseResult.data.httpError;
            return new HTTPError(message, statusCode, expose, { cause: error });
        }
    }
    return null;
}

export function extractHTTPError(error: MaybeHTTPError): {
    statusCode: number;
    message: string;
    headers: Record<string, string>;
} {
    const errorFromData = getHTTPErrorFromData(error);
    if (errorFromData) {
        return extractHTTPError(errorFromData);
    }

    const message = sanitizeError(error, 'Internal Server Error');

    if (!(error instanceof HTTPError)) {
        return {
            statusCode: 500,
            message,
            headers: {},
        };
    }

    return {
        statusCode: error.statusCode ?? 500,
        message,
        headers: error.headers,
    };
}

/**
 * Throws the error if it is not an exposable error.
 * Exposable errors can be handled in UI, non-exposable errors are unexpected and should be logged.
 */
export function throwIfNotExposable(error: unknown): asserts error is HTTPError {
    if (!(error instanceof HTTPError) || !error.expose) {
        throw error;
    }
}

/**
 * Runs promises in parallel and awaits all of them, even if some of them reject.
 * If there are any rejections, throws the most appropriate one among all.
 * Prioritizes 401, then 403, then the first error.
 *
 * Promise.all should be preferred for the sake of efficiency if there are no promises
 * that can reject with a 401 or 403 because it will exit early at the first rejection
 * and not await the other promises.
 *
 * @param promises an array of promises
 * @returns array of resolved promises if none of them are rejected
 */
export const resolveAllOrThrowBest: typeof Promise.all = async (promises: Promise<unknown>[]) => {
    const results = await Promise.allSettled(promises);

    const rejections = results
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason);

    // if all promises are resolved, return the results as if returning from Promise.all
    if (rejections.length === 0) {
        return results.map((result) => (result as PromiseFulfilledResult<unknown>).value);
    }

    const authenticationError = rejections.find(
        (err) => err instanceof HTTPError && err.statusCode === 401
    );
    if (authenticationError !== undefined) {
        throw authenticationError;
    }

    const authorizationError = rejections.find(
        (err) => err instanceof HTTPError && err.statusCode === 403
    );
    if (authorizationError !== undefined) {
        throw authorizationError;
    }

    throw rejections[0];
};

export function assertError(error: unknown): asserts error is Error {
    if (!(error instanceof Error)) {
        throw error;
    }
}
